<!-- client/src/views/Admin/AquariumManagement.vue -->
<template>
  <v-container>
      <v-row>
        <v-col>
          <h1>Aquarium Management
            <v-btn  variant="outlined" class="float-right" @click="refreshAquariums" color="primary">
              Refresh
            </v-btn>
          </h1>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="aquariums"
            :items-per-page="30"
            class="elevation-1"
          >
            <template v-slot:item.user_id="{ item }">
              <router-link :to="{ name: 'User Management', query: { id: item.user_id } }">
                {{ item.user_id }}
              </router-link>
            </template>
            <template v-slot:item.name="{ item }">
              <router-link :to="{ name: 'Aquarium Detail', params: { id: item.id } }">
                {{ item.name }}
              </router-link>
            </template>
            <template v-slot:item.log_count="{ item }">
              <router-link :to="{ name: 'Log Management', query: { aquarium_id: item.id } }">
                {{ item.log_count }}
              </router-link>
            </template>
            <template v-slot:item.active="{ item }">
              <v-chip :color="item.active ? 'green' : 'red'" dark>
                {{ item.active ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn @click="this.modalStore.openNewAquariumModal({ mode: 'admin', verb: 'edit', prefilledData: item })" color="primary" rounded="xl" size="x-small">Edit</v-btn>
              <v-btn @click="confirmDeleteAquarium(item.id)" color="error" rounded="xl" size="x-small">Delete</v-btn>
              <v-btn @click="confirmHardDeleteAquarium(item.id)" color="red darken-2" rounded="xl" size="x-small">Hard Delete</v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-btn variant="outlined" class="float-right" color="primary" @click="this.modalStore.openNewAquariumModal({mode:'admin', verb:'create', prefilledData:{name:''}})">
            Create New
          </v-btn>
        </v-col>
      </v-row>

      <v-dialog v-model="showDeleteModal" max-width="500px">
        <v-card>
          <v-card-title class="headline">Confirm Deletion</v-card-title>
          <v-card-text>Are you sure you want to delete this aquarium?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red darken-1" @click="deleteAquarium(confirmItemId)">Yes</v-btn>
            <v-btn color="green darken-1" @click="showDeleteModal = false">No</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showHardDeleteModal" max-width="500px">
        <v-card>
          <v-card-title class="headline">Confirm Hard Deletion</v-card-title>
          <v-card-text>
            <span style="color: red; font-weight: bold;">Warning:</span>
            This will permanently delete the aquarium and all associated logs and images. This action cannot be undone.<br>
            Are you sure you want to hard delete this aquarium?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red darken-1" @click="hardDeleteAquarium(confirmHardDeleteId)">Yes, Hard Delete</v-btn>
            <v-btn color="green darken-1" @click="showHardDeleteModal = false">No</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import { usePopupStore } from '@/store';
import { useModalStore } from '@/store';
import { useHead } from '@vueuse/head';

export default {
  name: 'AquariumManagement',
  setup() {
    useHead({
      title: 'Reeflendar.com - Aquarium Management',
      meta: [
        { name: 'description', content: 'Manage aquariums in Reeflendar.' },
        { name: 'keywords', content: 'admin, aquarium management, reef tank' },
      ],
    });

    return {
      popupStore: usePopupStore(),
      modalStore: useModalStore(),
    };
  },
  data() {
    return {
      aquariums: [],
      newAquarium: {
        user_id: '',
        name: '',
        description: '',
        image_url: ''
      },
      editItem: {},
      showDeleteModal: false,
      showEditModal: false,
      confirmItemId: null,
      showHardDeleteModal: false,
      confirmHardDeleteId: null,
      headers: [
        { title: 'ID', key: 'id' },
        { title: 'Active', key: 'active' },
        { title: 'User ID', key: 'user_id' },
        { title: 'Name', key: 'name' },
        { title: 'Description', key: 'description' },
        { title: 'Created At', key: 'created_at' },
        { title: 'Log Count', key: 'log_count' },
        { title: 'Actions', key: 'actions', sortable: false },
      ],
    };
  },
  methods: {
    async fetchAquariums() {
      try {
        const response = await axios.get('/api/aquariums/all');
        const userId = this.$route.query.user_id;
        const aquariumId = this.$route.query.id;
        if (userId) {
          this.aquariums = response.data.filter(aquarium => aquarium.user_id == userId);
        } else if (aquariumId) {
          this.aquariums = response.data.filter(aquarium => aquarium.id == aquariumId);
        } else {
          this.aquariums = response.data;
        }
      } catch (error) {
        this.popupStore.setMessage('Error fetching aquariums', error);
      }
    },
    refreshAquariums() {
      this.$router.push({ query: {} }).then(() => {
        this.fetchAquariums();
      });
    },
    confirmDeleteAquarium(aquariumId) {
      this.confirmItemId = aquariumId;
      this.showDeleteModal = true;
    },
    async deleteAquarium(aquariumId) {
      try {
        await axios.delete(`/api/aquariums/${aquariumId}`);
        this.aquariums = this.aquariums.filter(aquarium => aquarium.id !== aquariumId);
        this.showDeleteModal = false;
        this.fetchAquariums();
        this.$eventBus.emit('aquarium-updated');
      } catch (error) {
        this.popupStore.setMessage('Error deleting aquarium', error);
      }
    },
    confirmHardDeleteAquarium(aquariumId) {
      this.confirmHardDeleteId = aquariumId;
      this.showHardDeleteModal = true;
    },
    async hardDeleteAquarium(aquariumId) {
      try {
        await axios.delete(`/api/aquariums/${aquariumId}/hard_delete`);
        this.aquariums = this.aquariums.filter(aquarium => aquarium.id !== aquariumId);
        this.showHardDeleteModal = false;
        this.fetchAquariums();
        this.$eventBus.emit('aquarium-updated');
      } catch (error) {
        this.popupStore.setMessage("Error hard deleting aquarium", error);
      }
    },
  },
  mounted() {
    this.fetchAquariums();
    this.$eventBus.on('aquarium-updated', this.fetchAquariums);
  },
  beforeUnmount() {
    this.$eventBus.off('aquarium-updated', this.fetchAquariums);
  },
};
</script>